import { Link } from 'react-router-dom';
import { CartItem } from '../../models';
import { QuantityStepper } from '../../components/ui/QuantityStepper';
import { formatCurrency } from '../../utils/currency';
import { safeImageUrl } from '../../utils/url';
import { productPlaceholderIcon } from '../../utils/productPlaceholder';
import { GiftIcon, TrashIcon } from './CartIcons';
import { FOCUS_RING } from './cartStyles';
import { ItemStockIssue } from './useCartStockCheck';

interface CartItemRowProps {
  item: CartItem;
  issue: ItemStockIssue;
  onIncrease: () => void;
  onDecrease: () => void;
  onAdjust: () => void;
  onToggleGift: () => void;
  onRemove: () => void;
}

function describeVariant(item: CartItem): string {
  const variant = item.variantLabel
    ? item.variationTypeName
      ? `${item.variationTypeName}: ${item.variantLabel}`
      : item.variantLabel
    : '';
  return [item.colorName, variant].filter(Boolean).join(' · ');
}

function describeStockNote(item: CartItem, issue: ItemStockIssue): string | null {
  if (issue) return null;
  if (item.quantity < item.stockQuantity) return null;
  return item.stockQuantity === 1 ? 'Última peça disponível' : `Máximo disponível: ${item.stockQuantity}`;
}

function LinePrice({ item }: { item: CartItem }) {
  const unitPrice = item.promotionalPrice ?? item.price;
  const hasPromotion = item.promotionalPrice != null;
  const showBreakdown = item.quantity > 1 || hasPromotion;

  return (
    <div className="text-right leading-tight">
      {showBreakdown && (
        <p className="text-xs text-text-medium whitespace-nowrap">
          {hasPromotion && <span className="text-text-light line-through mr-1.5">R$ {formatCurrency(item.price)}</span>}
          {item.quantity > 1 && <span>{item.quantity} × R$ {formatCurrency(unitPrice)}</span>}
        </p>
      )}
      <p className="text-chocolate-dark font-extrabold text-lg whitespace-nowrap">
        R$ {formatCurrency(unitPrice * item.quantity)}
      </p>
    </div>
  );
}

export function CartItemRow({ item, issue, onIncrease, onDecrease, onAdjust, onToggleGift, onRemove }: CartItemRowProps) {
  const productHref = `/produtos/${item.productId}`;
  const isSoldOut = issue === 'soldOut';
  const variantText = describeVariant(item);
  const stockNote = describeStockNote(item, issue);

  return (
    <li className="bg-white rounded-xl p-3 sm:p-4 flex gap-3 sm:gap-4 shadow-sm">
      <Link
        to={productHref}
        tabIndex={-1}
        aria-hidden="true"
        className={`w-20 h-20 sm:w-24 sm:h-24 rounded-lg overflow-hidden bg-champagne-light flex-shrink-0 ${FOCUS_RING}`}
      >
        {item.imageUrl ? (
          <img
            src={safeImageUrl(item.imageUrl)}
            alt=""
            className={`w-full h-full object-cover ${isSoldOut ? 'grayscale opacity-50' : ''}`}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <img src={productPlaceholderIcon(item.productId)} alt="" className="w-10 h-10 opacity-70" />
          </div>
        )}
      </Link>

      <div className="flex-1 min-w-0 flex flex-col gap-1.5">
        <div className="flex flex-col gap-0.5">
          <Link
            to={productHref}
            className={`rounded font-bold text-sm sm:text-base line-clamp-2 hover:text-chocolate transition-colors w-fit max-w-full ${FOCUS_RING} ${
              isSoldOut ? 'text-text-medium' : 'text-text-dark'
            }`}
          >
            {item.productName}
          </Link>
          {variantText && <p className="text-text-medium text-xs line-clamp-1">{variantText}</p>}
        </div>

        {isSoldOut && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="bg-text-dark text-white text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-sm">
              Esgotado
            </span>
            <span className="text-xs text-text-medium">Remova da sacola para continuar.</span>
          </div>
        )}

        {issue === 'overStock' && (
          <div className="flex flex-wrap items-center gap-x-2 text-sm text-text-dark">
            <span>
              Só {item.stockQuantity === 1 ? 'resta' : 'restam'} {item.stockQuantity}{' '}
              {item.stockQuantity === 1 ? 'peça' : 'peças'}.
            </span>
            <button
              type="button"
              onClick={onAdjust}
              className={`min-h-11 px-1 rounded font-semibold text-chocolate-dark underline hover:text-chocolate ${FOCUS_RING}`}
            >
              Ajustar
            </button>
          </div>
        )}

        {!isSoldOut && (
          <div className="flex items-end justify-between gap-2 mt-0.5">
            <QuantityStepper
              size="lg"
              quantity={item.quantity}
              max={item.stockQuantity}
              onDecrease={onDecrease}
              onIncrease={onIncrease}
              disableDecrease={item.quantity <= 1}
            />
            <LinePrice item={item} />
          </div>
        )}

        {stockNote && <p className="text-xs text-text-medium">{stockNote}</p>}

        <div className="flex flex-wrap items-center justify-between gap-x-3">
          {!isSoldOut && (
            <div className="flex flex-col">
              <button
                type="button"
                onClick={onToggleGift}
                aria-pressed={!!item.isGift}
                className={`inline-flex items-center gap-1.5 min-h-11 text-sm font-medium px-3 py-1.5 rounded-full border w-fit transition-colors ${FOCUS_RING} ${
                  item.isGift
                    ? 'border-chocolate bg-champagne text-chocolate-dark'
                    : 'border-champagne-dark text-text-medium hover:border-chocolate/60 hover:text-chocolate'
                }`}
              >
                <GiftIcon />
                {item.isGift ? 'É presente' : 'Marcar como presente'}
              </button>
            </div>
          )}

          <button
            type="button"
            onClick={onRemove}
            className={`inline-flex items-center gap-1.5 min-h-11 px-3 rounded-lg text-sm font-medium text-text-medium hover:text-danger transition-colors ml-auto ${FOCUS_RING}`}
          >
            <TrashIcon />
            Remover
          </button>
        </div>
      </div>
    </li>
  );
}
